/**
 * data/useLockstopFeatures.ts — Lockstop features API client (28-09-2026,
 * richiesta utente — mirrors `useFeatures.ts` for the connector).
 *
 * Boundary rule: this folder NEVER imports three.js — it only fetches and
 * returns the pure `LockstopFeatures` contract.
 *
 * Fetches the hole geometry stored in the `lockstop_3d.features` JSONB
 * column: GET /api/modules/lock_calculator/lock-stops/{itemId}/features
 * (endpoint already generic server-side, no backend change needed — see
 * `catalog_controller.go`). Answers 204 when the item has no features, in
 * which case the lockstop is skipped and the scene renders without it.*/
import { useEffect, useState } from "react";

import { useApiHeaders } from "../../../../../hooks/useApiHeaders";
import type { ApiHeaders } from "../../../../../http/apiHeaders";
import type { LockstopFeatures } from "./types";

/** Session cache: an itemId is fetched once, `null` = no features available. */
const lockstopFeaturesCache = new Map<string, LockstopFeatures | null>();

async function fetchLockstopFeatures(itemId: string, apiHeaders: ApiHeaders): Promise<LockstopFeatures | null> {
  const cached = lockstopFeaturesCache.get(itemId);
  if (cached !== undefined) return cached;

  try {
    const response = await fetch(`/api/modules/lock_calculator/lock-stops/${itemId}/features`, {
      credentials: "include",
      redirect: "error",
      headers: apiHeaders,
    });
    if (response.status === 204) {
      lockstopFeaturesCache.set(itemId, null);
      return null;
    }
    if (!response.ok) throw new Error(`lockstop features request failed with status ${response.status}`);
    const features = (await response.json()) as LockstopFeatures;
    lockstopFeaturesCache.set(itemId, features);
    return features;
  } catch {
    // Un lockstop senza features non e' un errore: la scena rende senza di esso.
    lockstopFeaturesCache.set(itemId, null);
    return null;
  }
}

export interface LockstopFeaturesState {
  features: LockstopFeatures | null;
  isLoading: boolean;
}

/**
 * Hook features per il lockstop selezionato (v1: un solo lockstop per
 * calcolo, non uno per connettore — mirrors `useFeatures` ma per un solo id).
 * @param itemId Lockstop item id, o null se non selezionato.*/
export function useLockstopFeatures(itemId: string | null): LockstopFeaturesState {
  const apiHeaders = useApiHeaders();
  const [features, setFeatures] = useState<LockstopFeatures | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!itemId) {
      setFeatures(null);
      setIsLoading(false);
      return;
    }
    let isCurrent = true;
    setIsLoading(true);
    fetchLockstopFeatures(itemId, apiHeaders).then((resolved) => {
      if (!isCurrent) return;
      setFeatures(resolved);
      setIsLoading(false);
    });
    return () => {
      isCurrent = false;
    };
  }, [itemId, apiHeaders]);

  return { features, isLoading };
}
